import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/FormField';
import { FormSection } from '@/components/FormSection';
import { ErrorBanner } from '@/components';
import { PrimaryButton } from '@/components/PrimaryButton';
import { ContactFields } from './ContactFields';
import { UserPickerField } from './UserPickerField';
import type { UserRole } from '../../api/employees';
import type { LinkedUser } from '../../types';
import type { EmployeeFormData } from './CreateEmployeeForm';

interface CreateEmployeeFormContentProps {
    formData: EmployeeFormData;
    errors: {
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        email?: string;
        roleId?: string;
        userEmail?: string;
        general?: string;
    };
    roles?: UserRole[];
    rolesLoading: boolean;
    handleChange: (field: keyof EmployeeFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleToggleMainContact: () => void;
    handleSyncEmails: () => void;
    isFormValid: boolean;
    isSubmitting: boolean;
    // Linking an existing user account instead of sending an invite email
    canLinkUser?: boolean;
    handleSelectUser: (linkedUser: LinkedUser) => void;
    handleClearUser: () => void;
    isLinkedUserLocked?: boolean;
}

export function CreateEmployeeFormContent({
    formData,
    errors,
    roles,
    rolesLoading,
    handleChange,
    handleToggleMainContact,
    handleSyncEmails,
    isFormValid,
    isSubmitting,
    canLinkUser = false,
    handleSelectUser,
    handleClearUser,
    isLinkedUserLocked = false,
}: CreateEmployeeFormContentProps) {
    const { t } = useTranslation();

    return (
        <>
            <FormSection icon="contact" title={t('employees.createForm.sections.contact')}>
                <ContactFields
                    formData={formData}
                    errors={errors}
                    roles={roles}
                    rolesLoading={rolesLoading}
                    handleChange={handleChange}
                    handleToggleMainContact={handleToggleMainContact}
                />
            </FormSection>

            <FormSection icon="user" title={t('employees.createForm.sections.user')}>
                {canLinkUser ? (
                    <UserPickerField
                        linkedUser={formData.linkedUser}
                        onSelect={handleSelectUser}
                        onClear={handleClearUser}
                        error={errors.userEmail}
                        isLocked={isLinkedUserLocked}
                    />
                ) : (
                    <InviteEmailField
                        formData={formData}
                        error={errors.userEmail}
                        handleChange={handleChange}
                        handleSyncEmails={handleSyncEmails}
                    />
                )}
            </FormSection>

            <ErrorBanner hasError={errors.general !== undefined}>
                {errors.general}
            </ErrorBanner>

            <div className="pt-4 border-t border-border">
                <PrimaryButton
                    type="submit"
                    fullWidth
                    isLoading={isSubmitting}
                    disabled={isSubmitting || !isFormValid}
                >
                    {t('employees.createForm.submit')}
                </PrimaryButton>
            </div>
        </>
    );
}

// Invite email for a contact without a user account, with a shortcut that copies the contact email.
function InviteEmailField({
    formData,
    error,
    handleChange,
    handleSyncEmails,
}: {
    formData: EmployeeFormData;
    error?: string;
    handleChange: CreateEmployeeFormContentProps['handleChange'];
    handleSyncEmails: () => void;
}) {
    const { t } = useTranslation();

    return (
        <div>
            <FormField
                label=""
                labelKey="employees.createForm.inviteEmail"
                name="userEmail"
                type="email"
                value={formData.userEmailIsEmail ? formData.email : formData.userEmail}
                onChange={handleChange('userEmail')}
                error={error}
                placeholder={t('employees.createForm.inviteEmail')}
                required
            />
            {!formData.userEmailIsEmail && (
                <div className="flex mt-1 justify-end">
                    <button
                        type="button"
                        onClick={handleSyncEmails}
                        className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                        {t('employees.createForm.syncEmails')}
                    </button>
                </div>
            )}
        </div>
    );
}
